import { z } from "zod";
import { validateProjectPath } from "./builder";

/**
 * Agente de código (plugin "Agente Claude"): a pessoa descreve uma tarefa, a IA lê o projeto e
 * devolve um conjunto de alterações (criar, alterar, apagar arquivos). Nada é aplicado sozinho:
 * o editor mostra o diff de cada arquivo e a pessoa aplica (ou propõe como sugestão).
 */
export const AGENT_LIMITS = {
  maxTaskChars: 4_000,
  maxChanges: 12,
  maxFileChars: 60_000,
} as const;

export const AgentInput = z.object({
  projectId: z.uuid(),
  task: z.string().trim().min(5).max(AGENT_LIMITS.maxTaskChars),
  /** Arquivo aberto: entra primeiro no contexto. */
  activeFileId: z.string().max(64).optional(),
  selection: z.string().max(20_000).optional(),
});
export type AgentInput = z.infer<typeof AgentInput>;

export const AgentChange = z
  .object({
    path: z.string().superRefine((p, ctx) => {
      const error = validateProjectPath(p);
      if (error) ctx.addIssue({ code: "custom", message: error });
    }),
    action: z.enum(["create", "update", "delete"]),
    /** Conteúdo completo do arquivo depois da mudança (null em "delete"). */
    content: z.string().max(AGENT_LIMITS.maxFileChars).nullable(),
    reason: z.string().max(400),
  })
  .refine((c) => (c.action === "delete") === (c.content === null), {
    message: "create/update precisam de conteúdo; delete não",
  });
export type AgentChange = z.infer<typeof AgentChange>;

export interface AgentResponse {
  summary: string;
  changes: AgentChange[];
  /** Observações para a pessoa (riscos, o que testar, o que ficou de fora). */
  notes: string[];
  /** Mudanças descartadas pela validação (caminho inválido, arquivo inexistente…). */
  rejected: Array<{ path: string; reason: string }>;
  model: string;
}
